<template>

    <div>
        <div class="input-block">
            <label for="postcode">
                <slot name="postcode-label">Postcode</slot>
            </label>
            <input :class="{ 'input-error' : errors.has('address.postcode') }" :value="value.postcode"
                   @input="updatePostcode($event.target.name)" id="postcode" maxlength="20" name="address[postcode]"
                   placeholder="e.g. E9 7TA" ref="postcode" required
                   type="text">
            <span class="input-requirement error">{{ errors.get('address.postcode') }}</span>
            <span class="input-requirement error" v-show="requireValid && postcode_invalid">
                Sorry, that postcode was not recognised, please check it is correct. If you believe this is a mistake, please contact us!
            </span>
            <span class="input-requirement error">{{ errors.get('address.external_id') }}</span>
        </div>

        <input type="hidden" name="address[external_id]" id="external_id" ref="external_id" :value="value.external_id"/>

        <div v-if="addresses.length && !address_not_found">
            <div class="input-block">
                <label for="address-selector">
                    <slot name="choose-label">Choose Address</slot>
                </label>
                <select @change="fillAddress" id="address-selector" name="address-selector" required
                        v-model="selected_address">
                    <option disabled hidden selected value="">Choose</option>
                    <option :key="address.address" :value="index" v-for="(address, index) in addresses">
                        {{ address.address }}
                    </option>
                </select>
                <span @click="manualAddress" class="input-requirement label-button"
                      v-show="!postcode_invalid">Can't find your address?</span>
            </div>
        </div>

        <input :value="address_not_found" name="address[manual]" ref="manual" type="hidden">

        <div v-show="address_not_found">

            <div class="input-block">
                <label for="line1">
                    <slot name="street-label">Street Address</slot>
                </label>
                <input :class="{ 'input-error' : errors.has('address.line1') }" :value="value.line1"
                       @input="updateInput($event.target.name)" id="line1" maxlength="60"
                       name="address[line1]" placeholder="Street and number" ref="line1"
                       type="text">
                <input :class="{ 'input-error' : errors.has('address.line2') }" :value="value.line2"
                       @input="updateInput($event.target.name)" id="line2"
                       maxlength="60" name="address[line2]" placeholder="Flat, suite, unit, building, etc."
                       ref="line2" type="text">
                <span class="input-requirement error">{{ errors.get('address.line1') }}</span>
                <span class="input-requirement error">{{ errors.get('address.line2') }}</span>
            </div>

            <div class="input-block">
                <label for="city">
                    <slot name="town-label">Town / City</slot>
                </label>
                <input :class="{ 'input-error' : errors.has('address.city') }" :value="value.city"
                       @input="updateInput($event.target.name)" id="city" maxlength="50"
                       name="address[city]" ref="city"
                       type="text">
                <span class="input-requirement error">{{ errors.get('address.city') }}</span>
            </div>

            <div class="input-block">
                <label for="county">
                    <slot name="county-label">County</slot>
                </label>
                <input :class="{ 'input-error' : errors.has('address.county') }" :value="value.county"
                       @input="updateInput($event.target.name)" id="county" maxlength="50"
                       name="address[county]" ref="county"
                       type="text">
                <span class="input-requirement error">{{ errors.get('address.county') }}</span>
            </div>
        </div>
    </div>

</template>

<script>

    export default {
        name: "PostcodeFinder",

        props: [
            'value',
            'errors',
            'requireValid',
            'postcodeValid'
        ],

        data() {
            return {
                address_not_found: false,
                postcode_invalid: false,
                addresses: [],
                selected_address: '',
                utils_addresses: [],
                selected_utils_address: '',
                address_not_match: false,
                awaiting_confirmation: false,
            }
        },

        created() {
            this.debouncedQueryPostcode = _.debounce(this.queryPostcode, 500);
        },

        mounted() {
            if (this.hasManualAddressErrors() || (this.$props.value.manual && this.$props.value.manual === "true")) {
                // Trigger manual entry if hidden field has error
                this.$data.address_not_found = true;
            }
        },

        methods: {

            /**
             * Handles postcode input change
             * @param field Postcode field name
             */
            updatePostcode(field) {
                this.$emit('update:postcodeValid', false);
                this.updateInput(field);
                this.debouncedQueryPostcode(this.$refs.postcode.value);
            },

            /**
             * Triggers postcode look up if the postcode is valid
             */
            queryPostcode: function () {
                // Remove spaces
                let postcode = this.$refs.postcode.value.replace(/ /g, "");

                if (this.isValidPostcode(postcode) && !this.$data.address_not_found) {
                    this.lookupPostcode(postcode);
                }
            },

            /**
             * Checks if string matches postcode regex
             * @param postcode Postcode string
             */
            isValidPostcode(postcode) {
                return !!postcode.match(/^[a-zA-Z0-9]{1,4}\s?\d[a-zA-Z]{2}$/);
            },

            /**
             * Looks up postcode via API and stores the addresses. Triggers manual entry on error.
             * @param postcode Postcode string
             */
            lookupPostcode(postcode) {
                //Remove existing postcodes if there is any to avoid complications in the utilities address field
                this.$data.addresses = [];
                this.$data.utils_addresses = [];
                this.$data.selected_address = '';
                this.$data.selected_utils_address = '';
                this.$refs.external_id.value = null;

                this.updateInput();

                axios.get('/onboarding/address/' + postcode)
                    .then(response => {
                        if (!this.checkPostcodeResponse(response.data)) {
                            this.$data.addresses = [];
                            this.$data.postcode_invalid = true;
                            if (!this.requireValid) this.manualAddress();
                        }

                        this.$data.postcode_invalid = false;
                        this.$data.addresses = response.data.addresses;
                    })
                    .catch(error => {
                            this.$data.addresses = [];
                            this.$data.postcode_invalid = true;
                            if (!this.requireValid) this.manualAddress();
                        }
                    );
            },

            checkPostcodeResponse(data) {
                if (data === null || !"addresses" in data || data.addresses === null) {
                    return false;
                }

                return (data.addresses instanceof Array && data.addresses.length > 0)
            },

            /**
             * Updates the hidden fields with the values from the selected address
             */
            fillAddress() {
                let address = this.$data.addresses[this.$data.selected_address];

                this.$refs.line1.value = address.line1;
                this.$refs.line2.value = address.line2;
                this.$refs.city.value = address.city;
                this.$refs.county.value = null;
                this.$refs.external_id.value = address.external_id;

                this.$emit('update:postcodeValid', true);
                this.updateInput();
            },

            /**
             * Set up form fields for manual entry of address
             */
            manualAddress() {
                this.$data.address_not_found = true;
                this.$refs.line1.value = '';
                this.$refs.line2.value = '';
                this.$refs.city.value = '';
                this.$refs.county.value = '';
                this.$refs.external_id.value = null;
                this.$emit('update:postcodeValid', true);

                this.updateInput();
            },

            /**
             * Updates parent of field change and removes errors
             * @param field Name of field
             */
            updateInput(field) {
                if (this.$props.errors)
                    this.$props.errors.clear(field);

                let obj = {
                    postcode: this.$refs.postcode.value,
                    line1: this.$refs.line1.value,
                    line2: this.$refs.line2.value,
                    city: this.$refs.city.value,
                    county: this.$refs.county.value,
                    manual: this.$refs.manual.value,
                    external_id: this.$refs.external_id.value,
                };

                this.$emit('input', obj)
            },

            hasManualAddressErrors() {

                return this.$props.errors &&
                    !this.$props.errors.has(this.$refs.postcode.name) && (
                        this.$props.errors.has(this.$refs.line1.name) ||
                        this.$props.errors.has(this.$refs.line2.name) ||
                        this.$props.errors.has(this.$refs.city.name) ||
                        this.$props.errors.has(this.$refs.county.name)
                    )
            },
        }
    }
</script>
